import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { IconManage } from "../components/icons.js";
import { ShareLink } from "../components/ShareLink.js";
import { iconButtonClass, iconButtonStyle, Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { SPORT_LABEL } from "../lib/sport.js";
import { fetchEventMvps, fetchTournaments } from "../lib/tournaments.js";
import { formatDateRange, type EventMvp, type Tournament } from "../lib/types.js";
import { Avatar } from "../components/Avatar.js";
import { SectionHead } from "../components/profile.js";
import { CaptainBidLink } from "./AuctionCaptain.js";
import { FranchiseList } from "./Franchises.js";
import { SeedingLinks } from "./Seeding.js";
import { AuctionLinks } from "./TournamentRules.js";
import { EventRow, isFinished, TournamentBanner, useHousehold } from "./Tournaments.js";

/**
 * One tournament, at a URL of its own.
 *
 * A club night is announced as a whole — "Club Championship, the 27th, four events, pick
 * what you fancy" — and until now the only link that could be sent was to a single event.
 * Anyone sharing the weekend had to paste four of them, or send `/events` and hope the
 * reader scrolled to the right heading.
 *
 * Only this sport's events appear, because `fetchTournaments` already dropped the others.
 * A tournament that also runs pickleball reads here as though it does not, which is the
 * whole point of one app per sport.
 */
export function TournamentPage() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();

  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });

  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  return <TournamentDetail tournament={tournament} canOrganise={hasRole(member, "organiser")} />;
}

function TournamentDetail({
  tournament,
  canOrganise,
}: {
  tournament: Tournament;
  canOrganise: boolean;
}) {
  const events = tournament.events;
  const household = useHousehold();
  const finished = isFinished(tournament);

  // Only a finished tournament has any, so only a finished one asks.
  const { data: mvps = [] } = useQuery({
    queryKey: ["event-mvps", tournament.id],
    queryFn: () => fetchEventMvps(tournament.id),
    enabled: finished,
  });

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to="/events" className="micro mb-4 inline-block underline">
        ← All events
      </Link>

      {/* The same banner and rows as the events list, so following a shared link lands on
          something that looks like the place it came from. */}
      <header className="mb-2">
        <div className="-my-2.5 mb-1 flex items-center justify-between gap-3">
          <div className="micro">{SPORT_LABEL}</div>
          {/* Sends the whole night rather than one event — each of those has its own link. */}
          <div className="flex shrink-0 items-center">
            <ShareLink
              path={`/tournaments/${tournament.id}`}
              label="Share this tournament"
              title={tournament.name}
              text={`${tournament.name}, ${formatDateRange(tournament.startsOn, tournament.endsOn)}. Events and entry here:`}
            />
            {/* The organiser's way in, the same icon an event's page uses: everything an
                organiser changes on a tournament is on its own page now. */}
            {canOrganise ? (
              <Link
                to={`/tournaments/${tournament.id}/manage`}
                aria-label="Organise this tournament"
                title="Organise this tournament"
                className={iconButtonClass}
                style={iconButtonStyle}
              >
                <IconManage />
              </Link>
            ) : null}
          </div>
        </div>

        <TournamentBanner tournament={tournament} finished={finished} heading="h1" />
        {tournament.description ? (
          <p className="mt-3 whitespace-pre-line text-sm text-ink-muted">
            {tournament.description}
          </p>
        ) : null}
        {/* Said before anybody taps into an event, so the ask on the register screen is not
            the first they hear of it — as a facts line, not a bold sentence. "Teams are picked
            at an auction" is gone from here: the banner says Teams by auction. */}
        {tournament.contactRequired ? (
          <p className="font-mono mt-2 text-[11px] text-ink-muted">
            Entry needs a flat number and phone number on file
          </p>
        ) : null}
        {/* A captain's way to their phone screen while the auction runs, before anything else
            on the page: it is what they opened the app for. */}
        <CaptainBidLink tournament={tournament} />
      </header>

      <section>
        {events.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">No events yet.</p>
        ) : (
          events.map((event) => (
            <EventRow
              key={event.id}
              event={event}
              household={household}
              finished={finished}
              showFormat={!tournament.auction}
            />
          ))
        )}
      </section>

      {finished ? <MvpList tournament={tournament} mvps={mvps} /> : null}

      {/* Each team opens a page of its own, with the squads its finished pools produced. */}
      <FranchiseList tournament={tournament} />

      {/* Captains read the increments before they bid, and find their pool's lots here. */}
      <AuctionLinks tournament={tournament} />

      {/* A panelist's way in: shows nothing to anybody who is on no panel. */}
      <SeedingLinks tournament={tournament} />
    </main>
  );
}

/**
 * Who was MVP of each event, named when the tournament was finished. An event with nobody
 * named is left out rather than listed with a gap: not every event has a winner to name, and
 * a row saying so would read as something gone wrong.
 */
function MvpList({ tournament, mvps }: { tournament: Tournament; mvps: EventMvp[] }) {
  const events = tournament.events.filter((event) => mvps.some((mvp) => mvp.eventId === event.id));
  if (events.length === 0) return null;

  return (
    <section className="mt-8" data-testid="event-mvps">
      <SectionHead title="MVPs" />
      {events.map((event) => (
        <div
          key={event.id}
          className="border-b py-3"
          style={{ borderColor: "var(--color-line-muted)" }}
        >
          <div className="micro mb-1.5">{event.name}</div>
          {mvps
            .filter((mvp) => mvp.eventId === event.id)
            .map((mvp) => (
              <Link
                key={mvp.memberId}
                to={`/player/${mvp.memberId}`}
                className="flex min-h-[44px] items-center gap-3 hover:underline"
              >
                <Avatar name={mvp.displayName} url={mvp.avatarUrl} size={32} />
                <span className="flex-1 font-semibold">{mvp.displayName}</span>
                <span className="font-mono text-[11px] text-ink-muted">
                  {mvp.wins} {mvp.wins === 1 ? "win" : "wins"} · {mvp.pointDiff > 0 ? "+" : ""}
                  {mvp.pointDiff}
                </span>
              </Link>
            ))}
        </div>
      ))}
    </section>
  );
}
